import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import {
  EnablePermSessionRecording,
  EnableShiftRequestSessionRecording,
} from '~/constants/ff.posthog';
import { useAppSelector } from './useAppSelector';
import { useFeatureFlag } from './useFeatureFlag';
import { usePosthog } from './usePosthog';

// Temp shift request flow (ShiftRequestLayout). The date picker step lives under its own prefix
// and every later step under `/dashboard/multi-day`, so both have to be matched.
const SHIFT_REQUEST_PATHS = ['/dashboard/shift-request', '/dashboard/multi-day'];

// The Review step sends offices here when they have no card on file, and they come back to redo
// the request from scratch, so this counts as part of the request rather than an exit.
const PAYMENT_PATH = '/account/payment';

// The perm flow runs its own opt-in and its own stop. When it is recording too, leaving the
// stop to it avoids killing a recording it started on the very same route change.
const PERM_PATH = '/permanent-jobs';

/**
 * Opts the temp shift request flow into PostHog session recording, for the whole request and no
 * longer.
 *
 * Recording is disabled globally (`disable_session_recording`) and only opted into per flow, so
 * without this hook the only shift request sessions with a recording were the ones that had
 * already started recording elsewhere (perm request or sign up) — which both skewed the sample
 * and made playback open on the unrelated flow the recording had started on.
 *
 * The recording covers the request from the first step until it ends: a successful post (whose
 * confirmation popups land after the wizard has navigated away) or the office giving up. The
 * detour to add a payment method stays inside the request.
 */
export const useShiftRequestPosthogSessionRecording = () => {
  const location = useLocation();
  const posthog = usePosthog();
  const isEnabled = useFeatureFlag(EnableShiftRequestSessionRecording);
  const isPermRecordingEnabled = useFeatureFlag(EnablePermSessionRecording);

  // Posting navigates to the dashboard and only then shows its confirmation — the success
  // message, and the invite popups when the request had invites. Recording has to outlive the
  // wizard to catch them.
  const hasOpenConfirmation = useAppSelector(
    (state) =>
      !!state.message.message?.title ||
      state.multiday.jobsInvitePopup.showConfirmation ||
      state.multiday.jobsInvitePopup.showSelection,
  );

  const isRecordingRef = useRef(false);
  const sessionIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // PostHog rotates the session id on reset() (called on logout) and on session timeout.
    // Re-arm whenever the session changes, so a new session does not inherit the previous
    // one's recording decision.
    const currentSessionId = posthog.getSessionId();
    if (currentSessionId !== sessionIdRef.current) {
      sessionIdRef.current = currentSessionId;
      isRecordingRef.current = false;
    }

    const isOnFlow = SHIFT_REQUEST_PATHS.some((path) => location.pathname.startsWith(path));
    // Both of these only extend a request already in progress; neither ever starts one.
    const isOnPaymentDetour = isRecordingRef.current && location.pathname.startsWith(PAYMENT_PATH);
    const isOnConfirmation = isRecordingRef.current && hasOpenConfirmation;

    const shouldRecord = isEnabled && (isOnFlow || isOnPaymentDetour || isOnConfirmation);
    const handsOverToPerm = isPermRecordingEnabled && location.pathname.startsWith(PERM_PATH);

    if (shouldRecord) {
      // Read the live state rather than the ref: the perm hook stops the recording when it
      // leaves its own section, which would otherwise leave the rest of the request unrecorded.
      if (!posthog.isSessionRecordingEnabled()) posthog.startSessionRecording();
    } else if (isRecordingRef.current && !handsOverToPerm) {
      posthog.stopSessionRecording();
    }

    isRecordingRef.current = shouldRecord;
  }, [location.pathname, hasOpenConfirmation, posthog, isEnabled, isPermRecordingEnabled]);

  // Also stop when the app goes away mid-request (tab closed, logout).
  useEffect(() => {
    return () => {
      posthog.stopSessionRecording();
    };
  }, [posthog]);
};
